// Library Methods
import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { PaletteMode } from "@mui/material";

// Hooks
import { useUserSettings } from "src/services/users/hooks/useUserSettings";
import { useUpdateUserSettings } from "src/services/users/hooks/useUpdateUserSettings";

type ThemeModeContextType = {
  mode: PaletteMode;
  toggleMode: () => void;
};

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: "light",
  toggleMode: () => {},
});

export const useThemeModeContext = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error(
      "useThemeModeContext must be used within a ThemeModeProvider"
    );
  }

  return context;
};

type ThemeModeProviderType = {
  children: React.ReactNode;
};

// A context that holds the user's light/dark theme preference, which is
// saved in their user settings. Default mode is dark.
const ThemeModeProvider = ({ children }: ThemeModeProviderType) => {
  const { data: userSettings } = useUserSettings();
  const { mutate: mutateUserSettings } = useUpdateUserSettings();

  // The mode saved in the backend, once the settings have loaded
  const savedMode: PaletteMode | undefined = userSettings
    ? userSettings.isDarkMode
      ? "dark"
      : "light"
    : undefined;

  const [mode, setMode] = useState<PaletteMode>("dark");

  // Start from the saved mode, and follow it if it changes
  useEffect(() => {
    if (savedMode) setMode(savedMode);
  }, [savedMode]);

  // Only save the value to the backend once 3 seconds have passed
  useEffect(() => {
    if (!savedMode || mode === savedMode) return;

    const timeout = setTimeout(() => {
      mutateUserSettings(
        { isDarkMode: mode === "dark" },
        // Switch the display back if the save didn't work
        { onError: () => setMode(savedMode) }
      );
    }, 3000);

    return () => clearTimeout(timeout);
  }, [mode, savedMode, mutateUserSettings]);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeProvider;
